import { useCallback, useEffect, useRef, useState } from 'react';
import OrderEventController from '@/services/OrderEventController';
import { RETENTION_DAYS, rangeToQuery } from '../components/activity/activityRange.js';

// The live board's activity feed: what has happened in this city, in order.
//
// Two sources, and they have to agree. The socket carries every event from the
// moment the page connects; the REST backfill fills in the hours before that, so
// a reload does not start the timeline from nothing. Both deliver the same
// stored row — the backend broadcasts from inside the writer for exactly that
// reason (util/order-log.js).
//
// Note this hook attaches its own socket listener rather than going through
// useLiveSocket like the order handlers do. That hook's effect runs once with
// `[]` deps because it CREATES the socket, which is why its handlers have to
// reach state through refs. A consumer attaching one listener with a proper
// cleanup has no such problem, and a functional setState can never go stale.

// The board is a live view, not an archive — the backend keeps seven days and
// caps a backfill at 400. Holding more than this in memory buys nothing and
// makes every render walk a longer list.
const MAX_IN_MEMORY = 600;

export const useActivityFeed = ({ user, isMasterOrAdmin, socket }) => {
	const cityId = user?.cityLocation?.id;

	const [events, setEvents] = useState([]);

	// 'YYYY-MM-DD' each, empty when unset. Both optional: a `from` alone reads as
	// "since that day", a `to` alone as "up to the end of that day".
	const [range, setRange] = useState({ from: '', to: '' });
	// { key, events, error? } — the answer AND the range it answers, so a stale
	// window is never shown as if it were the one just asked for. null until the
	// first range is fetched.
	const [rangeData, setRangeData] = useState(null);
	// One value rather than a loading flag and an error flag, and it is only ever
	// written from inside the response callback. Setting state synchronously in an
	// effect body costs a second render pass before anything has happened.
	const [status, setStatus] = useState(() => (isMasterOrAdmin && cityId ? 'loading' : 'ready'));

	// The newest row already held, so a reconnect asks for the gap rather than
	// the whole window. A ref because the socket effect must not re-subscribe
	// every time an event lands.
	const newestAtRef = useRef(null);

	const append = useCallback(row => {
		if (!row?.id) {
			return;
		}

		setEvents(prev => {
			// The same row can arrive twice — a backfill racing the socket on
			// reconnect. Cheap to guard here, impossible to untangle later.
			if (prev.some(e => e.id === row.id)) {
				return prev;
			}
			const next = [...prev, row];
			return next.length > MAX_IN_MEMORY ? next.slice(next.length - MAX_IN_MEMORY) : next;
		});

		if (!newestAtRef.current || new Date(row.createdAt) > new Date(newestAtRef.current)) {
			newestAtRef.current = row.createdAt;
		}
	}, []);

	// One fetch, shared by the first load and by every reconnect. `since` is the
	// newest row already held, so a reconnect costs one small query instead of
	// the whole window; the server answers inclusively and `append` drops the
	// duplicate by id, which is what makes the boundary safe when two rows share
	// a timestamp to the millisecond.
	const backfill = useCallback(
		({ cityId: id, onDone }) =>
			OrderEventController.get({ cityId: id, since: newestAtRef.current }).then(res => {
				if (res?.error) {
					onDone?.('error');
					return;
				}
				(res.events || []).forEach(append);
				onDone?.('ready');
			}),
		[append],
	);

	// Backfill. Re-runs when the city changes, which for a master is a full
	// reload anyway — but not when `events` changes, or it would never settle.
	useEffect(() => {
		// No reset for a city change: switching city is a full window.reload() for
		// a master (see the store's login()), and a shop never gets here at all —
		// so clearing state here would be dead code that only cost a render.
		if (!isMasterOrAdmin || !cityId) {
			return;
		}

		let cancelled = false;

		backfill({
			cityId,
			onDone: next => {
				if (!cancelled) {
					setStatus(next);
				}
			},
		});

		return () => {
			cancelled = true;
		};
	}, [cityId, isMasterOrAdmin, backfill]);

	// The live half.
	useEffect(() => {
		if (!socket || !isMasterOrAdmin) {
			return;
		}

		socket.on('order-event', append);

		// socket.io reconnects on the SAME instance, so neither this effect nor
		// the backfill above re-runs when the connection drops and comes back —
		// and every event in between is simply never seen. The ref exists for
		// exactly this: ask for the gap, not the window.
		//
		// Keyed off a DROP having happened rather than off a connect count: the
		// socket may already be connected when this mounts (no `connect` event
		// ever fires), and the first connect after mount is the one the backfill
		// above already covered. A reconnect is always preceded by a disconnect,
		// so the flag is set exactly when there is a gap to fetch.
		let droppedSinceLastFetch = false;
		const onDisconnect = () => {
			droppedSinceLastFetch = true;
		};
		const onConnect = () => {
			if (!droppedSinceLastFetch) {
				return;
			}
			droppedSinceLastFetch = false;
			backfill({ cityId });
		};

		socket.on('connect', onConnect);
		socket.on('disconnect', onDisconnect);

		return () => {
			socket.off('order-event', append);
			socket.off('connect', onConnect);
			socket.off('disconnect', onDisconnect);
		};
	}, [socket, isMasterOrAdmin, append, backfill, cityId]);

	// A date range is a SERVER query, not a filter over what is on screen.
	//
	// The live list holds the last 24 hours capped at 400 rows, so filtering it
	// by date would answer "what happened yesterday" with whatever fraction of
	// yesterday happens to be in memory — usually nothing. Asking the server for
	// the window is the only version of this feature that is not a lie.
	//
	// Kept in its own state rather than merged into the live list: merging would
	// push today's events past MAX_IN_MEMORY and leave the board wrong after the
	// filter is cleared.
	//
	// Whether a range is on, and what it asks for, are DERIVED from the pickers —
	// no state, so there is nothing to write synchronously in the effect below.
	// The answer carries the range it belongs to, which is what says "this is the
	// window on screen" rather than the previous one still being shown while the
	// new one loads.
	const filteringByDate = !!(range.from || range.to);
	const rangeKey = `${range.from}|${range.to}`;
	const rangeQuery = filteringByDate ? rangeToQuery(range) : null;
	// The pickers pass through half-finished states while a date is typed, and a
	// backwards range is one of them: nothing to ask for, and not a failure.
	const rangeIsBackwards = filteringByDate && !rangeQuery;
	const rangeReady = !!rangeData && rangeData.key === rangeKey;

	useEffect(() => {
		if (!isMasterOrAdmin || !cityId || !rangeQuery) {
			return;
		}

		let cancelled = false;

		OrderEventController.get({ cityId, ...rangeQuery }).then(res => {
			if (cancelled) {
				return;
			}
			setRangeData(
				res?.error
					? { key: rangeKey, events: [], error: true }
					: { key: rangeKey, events: res.events || [] },
			);
		});

		return () => {
			cancelled = true;
		};
		// rangeQuery is rebuilt every render; rangeKey is the value that actually
		// changes when the admin picks a different day.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rangeKey, cityId, isMasterOrAdmin]);

	return {
		// While a range is on, the socket keeps filling the live list in the
		// background — so clearing the filter returns to a board that is current
		// rather than to a snapshot from when it was switched on.
		events: filteringByDate ? (rangeReady ? rangeData.events : []) : events,
		loading: filteringByDate ? !rangeReady && !rangeIsBackwards : status === 'loading',
		error: filteringByDate ? !!(rangeReady && rangeData.error) : status === 'error',
		range,
		setRange,
		filteringByDate,
		retentionDays: RETENTION_DAYS,
	};
};
